import { useCallback, useEffect, useState } from 'react'

import { useErrors } from '@src/hooks/useErrors'
import { ErrorCodes } from '@src/utils/constants.util'

const ERROR_DURATION = 3000

/**
 * Handles the quantity error message.
 * This error is shown when the user tries to add an invalid quantity to the cart.
 * It can be triggered by the API response or by changing the quantity input on the configurator.
 */
export function useQuantityError() {
  const error = useErrors()
  const [quantityErrorTimeout, setQuantityErrorTimeout] = useState<number | null>(null)

  // Show quantity error message for a short duration
  const showQuantityError = useCallback(() => {
    if (quantityErrorTimeout) return
    const timeout = window.setTimeout(() => {
      setQuantityErrorTimeout(null)
      error.reset()
    }, ERROR_DURATION)
    setQuantityErrorTimeout(timeout)
  }, [quantityErrorTimeout, error])

  // Catch invalid quantity error from the API
  useEffect(() => {
    if (error?.code === ErrorCodes.InvalidQuantity && !quantityErrorTimeout) {
      showQuantityError()
    }
  }, [error?.code, showQuantityError, quantityErrorTimeout])

  return {
    hasQuantityError: !!quantityErrorTimeout,
    showQuantityError,
  }
}
